import {
  ApiCostRun,
  useApiCosts,
  useApolloQuota,
  useHubSpotMatchTrace,
  useHubSpotStatus,
  useLandlordResearchTrace,
  useOutreachTrace,
  usePeopleDiscoveryTrace,
  useSiteHealthInvestigationTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";

const usd = (n: number): string => `$${n.toFixed(2)}`;

// Runtime column: sub-minute shows whole seconds, a minute+ shows "Xm Ys".
const fmtRuntime = (seconds: number | null): string => {
  if (seconds == null) return "-";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

// Apollo's free daily discovery quota resets at 00:00 UTC. Show how long until then
// so an operator who's hit the cap knows when it tops back up.
const apolloResetHint = (): string => {
  const now = new Date();
  const nextMidnightUtc = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1
  );
  const msLeft = nextMidnightUtc - now.getTime();
  const hours = Math.floor(msLeft / 3_600_000);
  const mins = Math.floor((msLeft % 3_600_000) / 60_000);
  const rel = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  return `resets in ${rel} (00:00 UTC)`;
};

// Ledger `source` values whose AI trace lives on its own endpoint. Mirrors the BE
// COST_SOURCES registry in api_costs_api.py - a source not listed here falls back
// to the landlord-research trace endpoint, which is where lead research stores its
// own. Keep in step when a new spending surface starts writing traces.
const CONTACTS_RANKING_SOURCE = "Contacts ranking";
const OUTREACH_SOURCE = "Outreach drafting";
const HUBSPOT_MATCH_SOURCE = "HubSpot matching";
const HEALTH_INVESTIGATION_SOURCE = "Site health investigation";

// Per-day spend strip. Deliberately plain inline bars rather than a chart
// dependency: the question it answers is "was there a spike, and roughly when",
// which does not need axes.
const SpendTrend = ({ days }: { days: { day: string; totalUsd: number }[] }) => {
  if (days.length === 0) return null;
  const max = Math.max(...days.map((d) => d.totalUsd), 0.0001);
  return (
    <div>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
        Daily spend
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 56 }}>
        {days.map((d) => (
          <OverlayTrigger
            key={d.day}
            placement="top"
            overlay={
              <Tooltip>
                {d.day}: {usd(d.totalUsd)}
              </Tooltip>
            }
          >
            <div
              style={{
                width: 10,
                // Always at least a sliver, so a low-but-nonzero day is visible
                // rather than reading as "nothing happened".
                height: Math.max(2, (d.totalUsd / max) * 56),
                background: "#0d6efd",
                opacity: 0.75,
                borderRadius: 1,
              }}
            />
          </OverlayTrigger>
        ))}
      </div>
    </div>
  );
};

const Costs = () => {
  const [days, setDays] = useState<number>(30);
  const [source, setSource] = useState<string>("");
  const costs = useApiCosts(days, source || null);
  const quota = useApolloQuota();
  const hubspot = useHubSpotStatus();
  const [traceOf, setTraceOf] = useState<ApiCostRun | null>(null);

  // The trace lives on a different endpoint per surface, so call every hook
  // (hooks can't be conditional) and enable only the one matching the run's
  // source; render whichever is enabled.
  const isContactsTrace = traceOf?.source === CONTACTS_RANKING_SOURCE;
  const isOutreachTrace = traceOf?.source === OUTREACH_SOURCE;
  const isHubSpotTrace = traceOf?.source === HUBSPOT_MATCH_SOURCE;
  const isHealthTrace = traceOf?.source === HEALTH_INVESTIGATION_SOURCE;
  const landlordTrace = useLandlordResearchTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null &&
      !isContactsTrace &&
      !isOutreachTrace &&
      !isHubSpotTrace &&
      !isHealthTrace
  );
  const peopleTrace = usePeopleDiscoveryTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isContactsTrace
  );
  const outreachTrace = useOutreachTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isOutreachTrace
  );
  const hubspotTrace = useHubSpotMatchTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isHubSpotTrace
  );
  const healthTrace = useSiteHealthInvestigationTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null && isHealthTrace
  );
  const traceQuery = isHealthTrace
    ? healthTrace
    : isHubSpotTrace
      ? hubspotTrace
      : isOutreachTrace
        ? outreachTrace
        : isContactsTrace
          ? peopleTrace
          : landlordTrace;

  const apollo = quota.data;
  const sourceRows = costs.data?.bySource
    ? Object.entries(costs.data.bySource)
        .filter(([, b]) => b.runCount > 0)
        .sort((a, b) => b[1].totalUsd - a[1].totalUsd)
    : [];
  // Apollo's counters only mean anything while lead sources are in view; filtering
  // to an operational source would otherwise leave a stale-looking credits block.
  const showApolloUsage = source === "" || sourceRows.some(([s]) => s.includes("Contacts"));

  return (
    <div className="container-fluid" style={{ padding: 16, fontSize: 13 }}>
      <h4 style={{ marginBottom: 4 }}>AI &amp; API costs</h4>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
        Every surface that spends on models or metered APIs. Figures are
        estimates computed from stored token and query counts, not billed
        amounts, so a rate change reprices history.
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <Form.Label style={{ margin: 0, fontSize: 12, color: "#888" }}>
          Window
        </Form.Label>
        <Form.Select
          size="sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{ width: 150 }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </Form.Select>
        <Form.Label style={{ margin: 0, fontSize: 12, color: "#888" }}>
          Source
        </Form.Label>
        <Form.Select
          size="sm"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          style={{ width: 220 }}
        >
          <option value="">All sources</option>
          {(costs.data?.sources ?? []).map((s) => (
            <option key={s.source} value={s.source}>
              {s.group} - {s.label}
            </option>
          ))}
        </Form.Select>
      </div>

      {costs.isLoading ? (
        <div style={{ color: "#888" }}>Loading costs...</div>
      ) : costs.isError || !costs.data ? (
        <Alert variant="danger">Could not load costs.</Alert>
      ) : (
        <div className="d-flex flex-column gap-4">
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 26, fontWeight: 600 }}>
                {usd(costs.data.totals.totalUsd)}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>
                total ({costs.data.totals.runCount} runs,{" "}
                {costs.data.totals.testRunCount} test,{" "}
                {costs.data.totals.failedRunCount} failed)
              </div>
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 600 }}>
                {usd(costs.data.totals.modelUsd)}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>model tokens</div>
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 600 }}>
                {usd(costs.data.totals.webSearchUsd)}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>
                web search ({costs.data.totals.webSearchCount} queries @{" "}
                {usd(costs.data.webSearchUsdPerQuery)})
              </div>
            </div>
            {showApolloUsage && (
              <>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 600 }}>
                    {costs.data.totals.apolloCredits ?? 0}
                  </div>
                  {/* A non-USD budget the totalUsd above deliberately omits. */}
                  <div style={{ fontSize: 11, color: "#888" }}>
                    Apollo credits
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 600 }}>
                    {costs.data.totals.apolloSearchCalls ?? 0}
                  </div>
                  <div style={{ fontSize: 11, color: "#888" }}>
                    Apollo searches
                  </div>
                </div>
              </>
            )}
          </div>

          <SpendTrend days={costs.data.byDay ?? []} />

          {sourceRows.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>
                By source
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {sourceRows.map(([label, b]) => (
                  <div key={label} style={{ fontSize: 12 }}>
                    <span style={{ color: "#888" }}>{label}:</span>{" "}
                    <strong>{usd(b.totalUsd)}</strong>{" "}
                    <span style={{ color: "#aaa" }}>
                      ({b.runCount} run{b.runCount === 1 ? "" : "s"})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metered non-model APIs. Apollo's discovery calls and enrichment
              credits are separate budgets from the USD figures above, and a dead
              HubSpot token is otherwise visible only in worker logs. */}
          {showApolloUsage && (
            <div className="d-flex flex-column gap-1" style={{ fontSize: 12 }}>
              <div style={{ fontSize: 11, color: "#888" }}>API usage</div>
              <div>
                {quota.isLoading ? (
                  <span style={{ color: "#888" }}>Loading Apollo quota...</span>
                ) : !apollo ? (
                  <span style={{ color: "#aaa" }}>Apollo quota unavailable.</span>
                ) : !apollo.configured ? (
                  <span style={{ color: "#b8860b" }}>
                    Apollo People Search is not configured on this environment -
                    the &quot;Find people&quot; actions will not run.
                  </span>
                ) : apollo.dayLimit != null ? (
                  <span>
                    <strong>Apollo discovery today:</strong>{" "}
                    {apollo.dayConsumed ?? 0} / {apollo.dayLimit} calls used
                    {apollo.dayLeft != null && (
                      <span style={{ color: "#888" }}>
                        {" "}
                        ({apollo.dayLeft} left)
                      </span>
                    )}
                    <span style={{ color: "#aaa" }}>
                      {" "}
                      - free discovery, 0 credits
                    </span>
                    <span style={{ color: "#888" }}> - {apolloResetHint()}</span>
                  </span>
                ) : (
                  <span style={{ color: "#aaa" }}>
                    Apollo quota unavailable
                    {apollo.error ? ` (${apollo.error})` : ""}.
                  </span>
                )}
              </div>
              {apollo && apollo.configured && (
                <div>
                  {apollo.enrichmentCredits ? (
                    <span>
                      <strong>Apollo enrichment:</strong>{" "}
                      {apollo.enrichmentCredits.used} credit
                      {apollo.enrichmentCredits.used === 1 ? "" : "s"} used
                      <span style={{ color: "#888" }}>
                        {" "}
                        ({apollo.enrichmentCredits.left} remaining
                        {apollo.enrichmentCredits.limit
                          ? ` of ${apollo.enrichmentCredits.limit}`
                          : ""}
                        )
                      </span>
                      <span style={{ color: "#aaa" }}>
                        {" "}
                        - paid, ~1 credit per contact revealed
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: "#aaa" }}>
                      <strong style={{ color: "#888" }}>
                        Apollo enrichment:
                      </strong>{" "}
                      credit balance unavailable on this plan.
                    </span>
                  )}
                </div>
              )}
              <div>
                {hubspot.isLoading ? (
                  <span style={{ color: "#888" }}>Checking HubSpot...</span>
                ) : !hubspot.data ? (
                  <span style={{ color: "#aaa" }}>
                    HubSpot status unavailable.
                  </span>
                ) : hubspot.data.status === "connected" ? (
                  <span>
                    <strong>HubSpot:</strong>{" "}
                    <span style={{ color: "#0f5132" }}>connected</span>
                    {hubspot.data.portalId && (
                      <span style={{ color: "#888" }}>
                        {" "}
                        - portal {hubspot.data.portalId}
                      </span>
                    )}
                    <span style={{ color: "#aaa" }}> - reads are free</span>
                  </span>
                ) : (
                  <span>
                    <strong>HubSpot:</strong>{" "}
                    <span style={{ color: "#b8860b" }}>
                      {hubspot.data.status === "not_configured"
                        ? "not connected"
                        : "unreachable"}
                    </span>
                    {hubspot.data.detail && (
                      <span style={{ color: "#888" }}>
                        {" "}
                        - {hubspot.data.detail}
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}

          {costs.data.recent.length === 0 ? (
            <div style={{ fontSize: 12, color: "#666" }}>
              No runs in this window.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table size="sm" striped hover style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Source</th>
                    <th>Subject</th>
                    <th style={{ textAlign: "right" }}>Web</th>
                    <th style={{ textAlign: "right" }}>Apollo calls</th>
                    <th style={{ textAlign: "right" }}>Credits</th>
                    <th style={{ textAlign: "right" }}>Runtime</th>
                    <th style={{ textAlign: "right" }}>Cost</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {costs.data.recent.map((r) => (
                    <tr key={r.id}>
                      <td>{new Date(r.createdAt).toLocaleString()}</td>
                      <td>{r.sourceLabel ?? r.source ?? "-"}</td>
                      <td>
                        {/* The subject's readable name; the raw ref stays on hover
                            for debugging, and is the fallback when it can't be
                            resolved (deleted, or absent from this env). */}
                        <span title={r.subjectRef ?? undefined}>
                          {r.subjectLabel ?? r.subjectRef ?? "-"}
                        </span>
                        {r.subjectRole && (
                          <Badge
                            bg="light"
                            text="dark"
                            style={{ marginLeft: 4, fontWeight: 500 }}
                          >
                            {r.subjectRole === "tenant" ? "Tenant" : "Landlord"}
                          </Badge>
                        )}
                        {r.isTest && (
                          <Badge bg="info" style={{ marginLeft: 4 }}>
                            test
                          </Badge>
                        )}
                        {r.failed &&
                          (r.error ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip
                                  style={{
                                    whiteSpace: "pre-line",
                                    textAlign: "left",
                                  }}
                                >
                                  {r.error}
                                </Tooltip>
                              }
                            >
                              <Badge bg="danger" style={{ marginLeft: 4 }}>
                                failed
                              </Badge>
                            </OverlayTrigger>
                          ) : (
                            <Badge bg="secondary" style={{ marginLeft: 4 }}>
                              failed
                            </Badge>
                          ))}
                      </td>
                      <td style={{ textAlign: "right" }}>{r.webSearchCount}</td>
                      <td style={{ textAlign: "right" }}>
                        {r.apolloSearchCalls != null ? r.apolloSearchCalls : "-"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {r.apolloCredits != null ? r.apolloCredits : "-"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {fmtRuntime(r.durationSeconds)}
                      </td>
                      <td style={{ textAlign: "right" }}>{usd(r.totalUsd)}</td>
                      <td>
                        {r.traceRunId && (
                          <Button
                            size="sm"
                            variant="link"
                            style={{ fontSize: 11, padding: 0 }}
                            onClick={() => setTraceOf(r)}
                          >
                            trace
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {costs.data.truncated && (
                <div style={{ fontSize: 11, color: "#aaa" }}>
                  Showing the most recent {costs.data.recent.length} runs;
                  totals above cover the full window.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Modal
        show={traceOf !== null}
        onHide={() => setTraceOf(null)}
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            {traceOf?.sourceLabel ?? "Run"} - AI trace
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {traceQuery.isLoading ? (
            <div style={{ fontSize: 12, color: "#888" }}>Loading trace...</div>
          ) : traceQuery.data ? (
            <AITraceViewer trace={traceQuery.data} />
          ) : (
            <div style={{ fontSize: 12, color: "#888" }}>
              No AI trace available for this run.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Costs;
